import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { CustomSpin as Spin, RangePickerFooter as Footer } from "_components";
import { useApp } from "_contexts";
import { isNonEmptyArray, isArray, disableAntdPickerDates, isEmpty, isEmptyArray } from "_helpers";

import { 
    Button,
    Checkbox,
    DatePicker,
    Divider,
    Form, 
    Popover, 
    Row, 
    Select,
    Space,
    Switch,
    TreeSelect 
} from 'antd';

import { popoverContent } from "../_popover";

import { ReactComponent as Delete } from "_assets/delete.svg";
import { ReactComponent as Info } from "_assets/info.svg";
import { ReactComponent as RightArrow } from "_assets/right_arrow.svg";

export function TopRequestFormContent({ selectedLeftMenu, reportingDataLength, resetValues, resetFields }) {
    const FormItem = Form.Item;
    const { SHOW_CHILD } = TreeSelect;
    const { RangePicker } = DatePicker;
    const form = Form.useFormInstance();

    const detailledReportValue = Form.useWatch('detailled_report', form);
    const basesValue = Form.useWatch('bases', form);
    const entitiesValue = Form.useWatch('entities', form);
    const constructDatesValue = Form.useWatch('constructDates', form);
    const displayDatesValue = Form.useWatch('displayDates', form);
    const { appConfig } = useApp();

    const formatFR = useRef("DD/MM/YYYY").current;
    const clearIconStyle = useRef({ height:"1em", width:"1em", fontSize:14 }).current;

    const bases = useMemo(() => appConfig?.reporting_bases ?? [], [appConfig?.reporting_bases]);
    const { entities, childOf, parentOf, baseOf, allEntitesCheckableValues } = useMemo(() => {
        if( isEmpty(appConfig?.entities) || basesValue === undefined ) return { entities: [], childOf: {}, parentOf: {}, baseOf: {}, allEntitesCheckableValues: [] };

        const basesArr = isArray(basesValue) ? basesValue : [basesValue];

        const entitiesOut = [],
            childOfOut = {}, // leaf -> parent
            parentOfOut = {}, // parent -> [...leaves]
            baseOfOut = {}, // base -> [...leaves]
            allCheckableValues = [];

        const pushUnique = ( arr, val ) => {
            if( val !== null && val!== undefined && !arr.includes(val) ) arr.push(val);
        }

        const pushLeaf = ( parentVal, leafVal, baseVal ) => {
            childOfOut[leafVal] = parentVal;

            parentOfOut[parentVal] ||= []
            pushUnique(parentOfOut[parentVal], leafVal);
            
            baseOfOut[baseVal] ||= []
            pushUnique(baseOfOut[baseVal], leafVal);
            pushUnique(baseOfOut[baseVal], parentVal);

            pushUnique(allCheckableValues, leafVal);
        };

        basesArr.forEach(baseKey => {
            const configRecord = appConfig.entities[baseKey];
            if( !configRecord ) return ;

            entitiesOut.push({
                title: (
                    <Divider orientation="left" className="mt-2 mb-1">{baseKey}</Divider>
                ),
                value: baseKey + '_divider',
                checkable: false
            });

            configRecord.forEach(row => {
                entitiesOut.push(row);
                const hasChildren = isNonEmptyArray(row.children);

                if( hasChildren ){
                    const recursiveWalk = ( currDepth, parentVal) => {
                        const hasC = isNonEmptyArray(currDepth.children);
                        if( hasC ){
                            currDepth.children.forEach(c => recursiveWalk(c, parentVal));
                            return ;
                        }
                        pushLeaf(parentVal, currDepth.value, baseKey);
                    }
                    row.children.forEach(child => recursiveWalk(child, row.value));
                    return ;
                }
                pushLeaf(null, row.value, baseKey);
            })
        })

        return {
            entities: entitiesOut,
            childOf: childOfOut,
            parentOf: parentOfOut,
            baseOf: baseOfOut,
            allEntitesCheckableValues: allCheckableValues
        };
    }, [appConfig?.entities, basesValue]);

    const lockedEntities = useMemo(() => {
        if( !detailledReportValue || entitiesValue === undefined || isEmptyArray(entitiesValue) ) return entities;
    
        const firstSelected = entitiesValue[0];
        const anchorParent = childOf[firstSelected] || firstSelected;

        const allowed = new Set([anchorParent, ...(parentOf[anchorParent] || [])]);

        const clone = ( node ) => {
            const hasChildren = isNonEmptyArray(node.children),
                isDivider = node.checkable === false;

            let retObj = {
                ...node,
                disabled: isDivider ? false : !allowed.has(node.value)
            };
            if( hasChildren ) retObj['children'] = node.children.map(clone);

            return retObj;
        };

        return entities.map(clone);
    }, [entities, entitiesValue, detailledReportValue, childOf, parentOf]);

    const isCheckedAll = isNonEmptyArray(entitiesValue) && entitiesValue.length === allEntitesCheckableValues.length;
    const indeterminate = isNonEmptyArray(entitiesValue) && entitiesValue.length < allEntitesCheckableValues.length;
    
    const [ treeSelectIsOpen, setTreeSelectIsOpen ] = useState(false);

    // Hack pour supprimer le ant-select-tree-switcher parasite
    useEffect(() => {
        if( treeSelectIsOpen === true ){
            const nodes = document.querySelectorAll('.no-switcher');
    
            nodes.forEach((el) => {
                const treenode = el.closest('.ant-select-tree-treenode');
                const switcher = treenode?.querySelector('.ant-select-tree-switcher');
                if (switcher) {
                    switcher.remove();
                }
            });
        }
    }, [bases, entities, treeSelectIsOpen]);

    // Hack pour changer dynamiquement le message d'erreur conditionnellement
    useEffect(() => {
        const fieldName = 'displayDates';
        const hasError = form.getFieldError(fieldName).length > 0;
        if( hasError ){
            form.validateFields([fieldName]);
        }
    }, [selectedLeftMenu, form]);

    const displayDatesRequiredValidation = (_, value) => {
        const message = selectedLeftMenu.includes('reporting') ? 'La Période du reporting est requise' : 'La Période de facturation est requise';
        if( isEmptyArray(value) ) {
            return Promise.reject(new Error(message));
        }
        return Promise.resolve();
    };

    const baseOfInclude = (baseKey, entity) => {
        return baseOf?.[baseKey] && baseOf[baseKey].includes(entity);
    }

    const onDetailledReportChange = (checked) => {
        const v = form.getFieldValue('bases');
        const w = form.getFieldValue('entities');
      
        const next = checked
            ? ( isArray(v) ? v?.[0] : v )                  // simple => scalaire
            : ( isArray(v) ? v : (v == null ? [] : [v]) ); // multiple => array

        const firstSelected = w?.[0];
        const anchorParent = childOf[firstSelected] || firstSelected;

        let allowed = [];
        if( anchorParent ) allowed = [anchorParent, ...(parentOf[anchorParent] || [])];

        let newValues = { detailled_report: checked, bases: next, entities: [], constructDates: [], displayDates: [] };
        const baseKey = isArray(next) ? next[0] : next;
        if( isNonEmptyArray(allowed) && baseOfInclude(baseKey, firstSelected) ){
            newValues.entities = [...new Set(allowed)];
        }

        form.setFieldsValue(newValues);

        if( reportingDataLength>0 ){ // Evite d'avoir un reporting détaillé après le fetch initial
            resetValues();
            resetFields();
        } 
    };

    const onBasesChange = (next) => {
        form.setFieldsValue({ bases: next, entities: [] });
    }

    const onCheckAllChange = useCallback(() => {
        form.setFieldsValue({ entities: isCheckedAll ? [] : allEntitesCheckableValues });
    }, [isCheckedAll, allEntitesCheckableValues, form]);

    const entitiesTreeData = useMemo(() => {
        if( isEmptyArray(lockedEntities) ) return [];

        const checkAllObj = {
            title: (
                <Divider orientation="left" className='no-switcher my-0'>
                    <Checkbox 
                        checked={isCheckedAll} 
                        indeterminate={indeterminate} 
                        onChange={onCheckAllChange}
                    >
                            {!isCheckedAll ? 'Tout sélectionner' : 'Tout désélectionner'}
                    </Checkbox>
                </Divider>
            ),
            value: 'checkAll_divider',
            checkable: false
        };

        if( !detailledReportValue ) return [checkAllObj, ...lockedEntities];
        return lockedEntities;
    }, [lockedEntities, isCheckedAll, indeterminate, detailledReportValue, onCheckAllChange]);

    return (
        <Row align="middle" justify="center">
            <Space wrap className="justify-content-center">
                <FormItem
                    label={<>Reporting détaillé ?</>}
                    name="detailled_report"
                    layout="vertical"
                    className="ant-custom-form-item"
                    style={{ height: '5rem' }}
                    wrapperCol={{ span: 8, offset: 8 }}
                >
                    <Switch onChange={onDetailledReportChange}/>
                </FormItem>

                <FormItem
                    label={<>Base</>} // Hack to disable tooltip
                    name="bases"
                    layout="vertical"
                    preserve={false}
                    className="ant-custom-form-item"
                    style={{ width: '12rem', height: '5rem' }}
                    rules={[{ required: true, message: 'La base est requise !' }]}
                >
                    <Select
                        options={bases}
                        placeholder="Sélection"
                        size="large"
                        className="w-100"
                        maxTagCount="responsive"
                        mode={detailledReportValue ? undefined : 'multiple' }
                        allowClear={{ clearIcon: <Delete {...clearIconStyle}/> }}
                        popupRender={(menu) => isNonEmptyArray(bases) ? menu : <Spin />}
                        filterOption={(inputValue, option) =>
                            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                        }
                        onChange={onBasesChange}
                    />
                </FormItem>

                <FormItem
                    label={<>Entités</>}
                    name="entities"
                    layout="vertical"
                    className="ant-custom-form-item"
                    style={{ width: '18rem', height: '5rem' }}
                    validateTrigger="onChange"
                    rules={[{ required: true, message: "L'entité est requise !" }]}
                >
                    <TreeSelect 
                        treeData={entitiesTreeData}
                        treeCheckable={true}
                        showCheckedStrategy={SHOW_CHILD}
                        placeholder="Sélection"
                        maxTagCount="responsive"
                        size="large"
                        allowClear={{ clearIcon: <Delete {...clearIconStyle}/> }}
                        filterTreeNode={(inputValue, treeNode) => {
                            const title = treeNode.title;
  
                            if( typeof title === 'string' ) {
                                return title.toLowerCase().includes(inputValue.toLowerCase());
                            }

                            // if( React.isValidElement(title) ) {
                            //     const text = title.props.children;
                            //     return typeof text === 'string' && text.toLowerCase().includes(inputValue.toLowerCase());
                            // }

                            return false;
                        }}
                        onOpenChange={() => setTreeSelectIsOpen(prevValue => !prevValue)}
                    />
                </FormItem>

                {
                    selectedLeftMenu && selectedLeftMenu.includes('reporting') && detailledReportValue!==true
                    &&
                        <FormItem
                            label={
                                <>
                                    Période de la mission
                                    <Popover content={popoverContent}>
                                        <Info height={15} width={15} className={"ms-1 info-svg"}/>
                                    </Popover>
                                </>
                            }
                            name="constructDates"
                            layout="vertical"
                            className="ant-custom-form-item"
                            style={{ width: '20rem', height: '5rem' }}
                        >
                            <RangePicker
                                disabledDate={disableAntdPickerDates}
                                renderExtraFooter={() =>
                                    <Footer 
                                        itemName="constructDates"
                                        itemValues={constructDatesValue}
                                    />
                                }
                                size="large"
                                format={formatFR}
                                allowClear={{ clearIcon: <Delete {...clearIconStyle}/> }}
                                // onCalendarChange={(dates) => onChange(dates, "constructDates")} // fired when one of two dates changes
                                // onChange={(value) => onClear(value, "constructDates")}
                            />
                        </FormItem>
                }

                {
                    detailledReportValue!==true
                    &&
                        <FormItem
                            label={
                                selectedLeftMenu
                                &&
                                    selectedLeftMenu.includes('reporting')
                                    ?
                                        <>Période du reporting</>
                                    :
                                        <>Période de facturation</>
                            }
                            name="displayDates"
                            layout="vertical"
                            className="ant-custom-form-item"
                            style={{ width: '20rem', height: '5rem' }}
                            rules={[{ 
                                required: true,
                                validator: displayDatesRequiredValidation
                            }]}
                        >
                            <RangePicker
                                disabledDate={disableAntdPickerDates}
                                renderExtraFooter={() =>
                                    <Footer 
                                        itemName="displayDates"
                                        itemValues={displayDatesValue}
                                    />
                                }
                                size="large"
                                format={formatFR}
                                allowClear={{ clearIcon: <Delete {...clearIconStyle}/> }}
                            />
                        </FormItem>
                }

                <Form.Item 
                    label={null} 
                    layout="vertical" 
                    className="d-flex align-items-end"
                    style={{ height: '5rem', paddingBottom: '0.7rem' }}
                >
                    <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        icon={
                            <RightArrow 
                                height={20} 
                                width={20} 
                                viewBox="0 0 45 50"
                                style={{ display: 'block', paddingLeft: '3px' }}
                            />
                        }
                    />
                </Form.Item>
            </Space>
        </Row>
    );
}